import { KeyboardEvent, useCallback, useRef } from "react";

export function DashboardTabStrip<T extends string>({
  tabs,
  activeTab,
  onSelect,
  ariaLabel = "Dashboard tabs",
}: {
  tabs: ReadonlyArray<{ readonly id: T; readonly label: string }>;
  activeTab: T;
  onSelect: (id: T) => void;
  ariaLabel?: string;
}) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTabAtIndex = useCallback(
    (index: number) => {
      const nextTab = tabs[index];
      if (!nextTab) {
        return;
      }

      onSelect(nextTab.id);
      tabRefs.current[index]?.focus();
    },
    [onSelect, tabs],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (tabs.length === 0) {
        return;
      }

      let nextIndex = index;
      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (index - 1 + tabs.length) % tabs.length;
          break;
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (index + 1) % tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      focusTabAtIndex(nextIndex);
    },
    [focusTabAtIndex, tabs.length],
  );

  return (
    <div
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className="dashboard-tab-strip-bar flex gap-3 overflow-x-auto border-b border-white/10 px-2"
      role="tablist"
      data-oid="htc6c2d"
    >
      {tabs.map((tab, index) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            aria-selected={isActive}
            className={[
              "typography-tag shrink-0 whitespace-nowrap border-b-2 pb-3 uppercase transition-colors",
              isActive
                ? "dashboard-tab-item--active border-sky-500 text-white"
                : "dashboard-tab-item--inactive border-transparent text-slate-500 hover:text-slate-300",
            ].join(" ")}
            style={{ letterSpacing: "0.18em" }}
            key={tab.id}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            role="tab"
            tabIndex={isActive ? 0 : -1}
            type="button"
            data-oid="-z:qed:"
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
